import "./GroupOverview.scss";

import clsx from "clsx";
import { Fragment, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Attention,
  Info,
  PhoneAttention,
  PhoneAttentionBold,
  Share,
} from "@/assets/icons/icons";
import { useGetLinks } from "@/components/FriendsWithBenefits/SocialLinks";
import { Button } from "@/components/Interface/Button/Button";
import { ClipboardField } from "@/components/Interface/ClipboardFiled/ClipboardField";
import { Modal } from "@/components/Interface/Modal/Modal";
import { Pill } from "@/components/Interface/Pill/Pill";
import { Tooltip } from "@/components/Interface/Tooltip/Tooltip";
import { PortalHeading } from "@/components/Portal/PortalHeading/PortalHeading";
import { useIsMobile } from "@/hooks/useIsMobile";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useGetCustomer,
  useGetCustomerTribe,
  useGetDetailTribe,
  useGetInvitationLink,
  useLeaveTribe,
} from "@/services/api";

import { GroupOverviewContent } from "./GroupOverviewContent";
import { NoEligibleProductContent } from "./NoEligibleProduct";

const GroupOverview = () => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits",
  });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showLeaveTribeModal, setShowLeaveTribeModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  const { data: customer } = useGetCustomer();
  const { data: customerTribe } = useGetCustomerTribe();
  const { data: tribeDetail } = useGetDetailTribe(customerTribe?.tribeId || "");
  const { data: invitationLink } = useGetInvitationLink({
    query: {
      queryKey: ["invitationLink", customerTribe?.tribeId],
      enabled: !!customerTribe?.tribeId,
    },
  });

  const { mutate: leaveTribe } = useLeaveTribe();

  const members = tribeDetail?.memberInfo || [];
  const isTribeFull = tribeDetail?.maxMembersReached;

  const currentDiscount = tribeDetail?.tribeDiscount
    ? `${tribeDetail.tribeDiscount}%`
    : "0%";

  const userIsActive = !members.some(
    (member) => member.inactiveDays && customer?.customerId === member.userId,
  );

  const shareText = `${t("groupOverview.shareSubHeadline").replace(/<br\s*\/?>/g, "\n")}\n\n${t(
    "groupOverview.shareText",
  )}\n`;

  const links = useGetLinks(
    t("groupOverview.shareTitle"),
    shareText,
    invitationLink,
  );

  const lastCharS =
    customer?.firstName?.[customer?.firstName.length - 1] === "s";

  const handleShare = async () => {
    if (!invitationLink) return;

    // Share API for mobile
    if (navigator.share) {
      try {
        await navigator.share({
          title: t("groupOverview.shareTitle"),
          // need to add invitation link for mobile
          text: shareText + "\n" + invitationLink,
        });
      } catch (error) {
        // Fallback to clipboard if share was cancelled or failed
        navigator.clipboard.writeText(invitationLink);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(invitationLink);
    }
  };

  const confirmLeaveTribe = () => {
    leaveTribe(undefined, {
      onSuccess: () => {
        setShowLeaveTribeModal(false);
        navigate(portalRoutes.friendsWithBenefits.createGroup);
      },
    });
  };

  return (
    <div
      id="group-overview-wrapper"
      className={clsx(isMobile ? "flex flex-col" : "tile-width-wrapper")}
    >
      <PortalHeading heading={customer?.firstName || ""}>
        <p className="mb-14 mt-8 md:mt-10">{t("groupOverview.subheading")}</p>
      </PortalHeading>
      <div className="management-tile active">
        <div
          className={clsx(
            "mb-8 flex items-center justify-between",
            isMobile && "relative",
          )}
        >
          <p className="text-4xl font-semibold text-secondary-100">
            {`${customer?.firstName}${lastCharS ? "'" : "'s"} ${t("groupOverview.friendsGroup")}`}
          </p>
          {isMobile ? (
            <div className="right-0 top-0 p-2">
              <Info
                className="text-primary-100"
                onClick={() => setShowInstructionsModal(true)}
              />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-8">
                <div
                  className="cursor-pointer"
                  onClick={() => setShowInstructionsModal(true)}
                >
                  <Pill
                    className="flex bg-primary-100 text-white"
                    icon={<Info className="h-4 w-4" />}
                    text={t("groupOverview.howItWorks")}
                  />
                </div>

                <p
                  className="cursor-pointer text-sm text-primary-100 underline"
                  onClick={() => {
                    setShowLeaveTribeModal(true);
                  }}
                >
                  {t("groupOverview.leaveTribe")}
                </p>
              </div>
            </>
          )}
        </div>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {/* Left Column - needs to be enclosed in div not fragment for layout reason */}
          <div>
            {userIsActive ? (
              <GroupOverviewContent
                tribeDetail={tribeDetail!}
                currentDiscount={currentDiscount}
                isTribeFull={isTribeFull}
              />
            ) : (
              <NoEligibleProductContent />
            )}

            {/* Invitation Link Section */}
            {isMobile ? (
              <Button
                className="accent"
                onClick={handleShare}
                disabled={isTribeFull}
              >
                <div className="flex items-center justify-center gap-4">
                  <Share />
                  {t("groupOverview.share")}
                </div>
              </Button>
            ) : (
              <>
                <div className="border-1px relative mb-8 flex items-center rounded border-primary-100">
                  <ClipboardField
                    content={invitationLink || ""}
                    successMessage={t("groupOverview.linkCopied")}
                    errorMessage={t("groupOverview.linkCopyError")}
                  />
                </div>
                <div className="mb-8 mt-6 flex gap-4">
                  {links.map(({ icon, link }) => (
                    <a
                      key={link}
                      className={clsx(
                        "flex h-8 w-8 items-center justify-center rounded-full bg-primary-100",
                        isTribeFull &&
                          "pointer-events-none cursor-default opacity-25",
                      )}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {icon}
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right Column */}
          <div className="border-l-2 border-secondary-100 pl-8">
            {/* Members Table */}
            <h3 className="mb-2 text-lg text-secondary-100">
              {t("groupOverview.tableTitle")}
            </h3>
            <div className="mb-8 flex flex-wrap gap-2 whitespace-nowrap">
              <Pill
                className="flex border border-highlight-100 bg-highlight-25 text-primary-100"
                text={`${currentDiscount} ${t("groupOverview.discount")}`}
              />
              <Pill
                className="flex border border-highlight-100 bg-highlight-25 text-primary-100"
                text={t("groupOverview.members", {
                  count: tribeDetail?.membersCounter || 0,
                })}
              />
              <Pill
                className="flex border border-highlight-100 bg-highlight-25 text-primary-100"
                text={t("groupOverview.tariffs", {
                  count: tribeDetail?.eligibleSimCount || 0,
                })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-sm text-primary-100">
                {t("groupOverview.name")}
              </div>
              <div className="text-sm text-primary-100">
                {t("groupOverview.eligibleProducts")}
              </div>
              {/* Put current user on top of member list */}
              {[...members]
                .sort((a, b) => {
                  if (customer?.customerId === a.userId) return -1;
                  if (customer?.customerId === b.userId) return 1;

                  return 0;
                })
                .map((member) => {
                  const itsMe = customer?.customerId === member.userId;

                  return (
                    <Fragment key={member.userId}>
                      <div className={clsx(itsMe && "font-semibold")}>
                        {member.firstName || "New Friend"}
                      </div>
                      <div className="text-bodyRegular flex items-center gap-2 text-black">
                        <span className={clsx(itsMe && "font-semibold")}>
                          {member.eligibleSims}
                        </span>
                        {member.onpEligibleSims !== undefined &&
                          member.onpEligibleSims > 0 && (
                            <Tooltip
                              className="min-w-56"
                              content={
                                <div className="xsPlus:w-32 smPlus:w-40">
                                  <Trans
                                    t={t}
                                    i18nKey="groupOverview.onpHint"
                                    components={[
                                      <span
                                        key="devices"
                                        className="font-semibold text-primary-100"
                                      >
                                        <br />
                                      </span>,
                                    ]}
                                    values={{
                                      devices: member.onpEligibleSims,
                                    }}
                                  />
                                </div>
                              }
                              leftOnMobile
                            >
                              <div className="flex items-center">
                                {itsMe ? (
                                  <PhoneAttentionBold />
                                ) : (
                                  <PhoneAttention />
                                )}
                                <span className="text-primary-100">
                                  ({member.onpEligibleSims})
                                </span>
                              </div>
                            </Tooltip>
                          )}
                        {member.inactiveDays !== undefined &&
                          member.inactiveDays > 0 && (
                            <Tooltip
                              className="min-w-56"
                              content={
                                <Trans
                                  t={t}
                                  i18nKey="groupOverview.inactiveWarning"
                                  components={[
                                    <span
                                      key="days"
                                      className="font-semibold text-primary-100"
                                    />,
                                  ]}
                                  values={{
                                    days: member.inactiveDays,
                                  }}
                                />
                              }
                              leftOnMobile
                            >
                              <Attention />
                            </Tooltip>
                          )}
                      </div>
                    </Fragment>
                  );
                })}
            </div>
          </div>
        </div>
        {showLeaveTribeModal && (
          <Modal
            title={t("groupOverview.leaveTribeConfirmationTitle")}
            onClose={() => setShowLeaveTribeModal(false)}
          >
            <div className="flex flex-col gap-6">
              <p className="mb-8 text-lg font-normal text-black">
                {t("groupOverview.leaveTribeConfirmationText")}
              </p>
              <div className="flex flex-col justify-end gap-4 smPlus:flex-row">
                <Button
                  fullWidth
                  className="accent inverted"
                  onClick={() => setShowLeaveTribeModal(false)}
                >
                  {t("groupOverview.cancel")}
                </Button>
                <Button
                  fullWidth
                  className="accent"
                  onClick={confirmLeaveTribe}
                >
                  {t("groupOverview.confirmLeave")}
                </Button>
              </div>
            </div>
          </Modal>
        )}
        {isMobile && (
          <Button
            className="accent inverted mt-8"
            onClick={() => {
              setShowLeaveTribeModal(true);
            }}
          >
            {t("groupOverview.leaveTribe")}
          </Button>
        )}
        {showInstructionsModal && (
          <Modal
            title={t("groupOverview.howItWorksModal.title")}
            onClose={() => setShowInstructionsModal(false)}
          >
            <h2 className="mb-4 text-base text-primary-100">
              {t("groupOverview.howItWorksModal.eligibleSubscriptionsQuestion")}
            </h2>
            {t("groupOverview.howItWorksModal.eligibleSubscriptionsAnswer")
              .split("\n\n")
              .map((paragraph, index) => (
                <p key={index} className="text-base font-normal">
                  {paragraph}
                </p>
              ))}

            <h2 className="mb-4 mt-6 text-base text-primary-100">
              {t("groupOverview.howItWorksModal.flatMobileInactiveQuestion")}
            </h2>
            {t("groupOverview.howItWorksModal.flatMobileInactiveAnswer")
              .split("\n\n")
              .map((paragraph, index) => (
                <p key={index} className="text-base font-normal">
                  {paragraph}
                </p>
              ))}

            <h2 className="mb-4 mt-6 text-base text-primary-100">
              {t(
                "groupOverview.howItWorksModal.numberPortingInProgressQuestion",
              )}
            </h2>
            {t("groupOverview.howItWorksModal.numberPortingInProgressAnswer")
              .split("\n\n")
              .map((paragraph, index) => (
                <p key={index} className="text-base font-normal">
                  {paragraph}
                </p>
              ))}

            <h2 className="mb-4 mt-6 text-base text-primary-100">
              {t("groupOverview.howItWorksModal.changingSubscriptionQuestion")}
            </h2>
            <p className="text-base font-normal">
              {t("groupOverview.howItWorksModal.changingSubscriptionAnswer")}
            </p>

            <h2 className="mb-4 mt-6 text-base text-primary-100">
              {t("groupOverview.howItWorksModal.joinMoreGroupsQuestion")}
            </h2>
            <p className="text-base font-normal">
              {t("groupOverview.howItWorksModal.joinMoreGroupsAnswer")}
            </p>

            <h2 className="mb-4 mt-6 text-base text-primary-100">
              {t(
                "groupOverview.howItWorksModal.maxMemberLimitExceededQuestion",
              )}
            </h2>
            <p className="text-base font-normal">
              {t("groupOverview.howItWorksModal.maxMemberLimitExceededAnswer")}
            </p>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default GroupOverview;
